<template>
    <tippy
        ref="root"
        class="hts-popover"
        theme="hts-popover"
        :trigger="trigger"
        :animation="animation"
        :placement="placement"
        :delay="delay"
        :arrow="arrow"
        :max-width="maxWidth"
        :move-transition="moveTransition"
        :on-show="onShow"
        :on-hide="onHide"
        :z-index="zIndex"
        :hide-on-click="hideOnClick"
        :append-to="appendTo"
        :interactive="interactive"
        :allow-h-t-m-l="allowHTML"
        :offset="offset"
        :on-click-outside="onClickOutside">
        <div
            class="hts-popover__reference"
            :class="referenceClass">
            <slot name="reference" />
        </div>
        <template
            v-if="!disabled"
            #content>
            <div
                class="hts-popover__content"
                :class="contentClass">
                <slot />
            </div>
        </template>
    </tippy>
</template>

<script lang="ts" setup>
import { roundArrow } from 'tippy.js'
import 'tippy.js/dist/svg-arrow.css'
import 'tippy.js/animations/scale.css'

const root = templateRef<any>('root', null)
const props = defineProps({
  modelValue: Boolean,
  trigger: {
    type: String,
    default: 'click'
  },
  interactive: {
    type: Boolean,
    default: true
  },
  animation: {
    type: String,
    default: 'scale'
  },
  allowHTML: Boolean,
  hideOnClick: {
    type: [String, Boolean],
    default: true
  },
  appendToBody: {
    type: Boolean,
    default: true
  },
  placement: {
    type: String,
    default: 'bottom'
  },
  delay: {
    type: [Number, Array],
    default: 0
  },
  arrow: {
    type: [String, Object, Boolean],
    default: roundArrow
  },
  duration: {
    type: Array,
    default: () => [300, 50]
  },
  maxWidth: {
    type: [String, Number],
    default: 350
  },
  moveTransition: {
    type: String,
    default: ''
  },
  zIndex: {
    type: Number,
    default: 9999
  },
  offset: {
    type: Array,
    default: () => [0, 10]
  },
  onClickOutside: Function,
  referenceClass: String,
  contentClass: String,
  disabled: {
    type: Boolean,
    default: false
  }
})
const { modelValue, appendToBody } = toRefs(props)
const emits = defineEmits(['update:modelValue', 'show', 'hide'])
const onShow = () => {
  emits('update:modelValue', true)
  emits('show')
}
const onHide = () => {
  emits('update:modelValue', false)
  emits('hide')
}
const show = () => {
  if (root.value) {
    return root.value.show()
  }
}
const hide = () => {
  if (root.value) {
    return root.value.hide()
  }
}
watch(
  () => modelValue.value,
  (val: boolean) => {
    if (val) {
      return show()
    }
    hide()
  }
)
const appendTo = computed(() => {
  if (process.client && appendToBody.value) {
    return () => document.body
  }
  return 'parent'
})
onMounted(() => {
  if (modelValue.value) {
    show()
  }
})
defineExpose({
  show,
  hide
})
</script>
